/**
 * "Scan license": read the barcode on the back of a driver's license or ID (PDF417, AAMVA) with the phone or
 * tablet camera, or a handheld 2D scanner, and fill in the person. The deputy checks what it read before it's used.
 * Nothing leaves the device except what the deputy puts in the form (and a name lookup to find them in BlueHQ).
 */
import { useEffect, useRef, useState } from 'react';
import { parseAamva, type LicenseData } from '@civilos/domain';
import { api } from './api.ts';
import { Icon } from './icons.tsx';

const camSupported = () => typeof window !== 'undefined' && 'BarcodeDetector' in window && !!navigator.mediaDevices?.getUserMedia;

export function LicenseScanButton({ onScan, label = 'Scan license', small }: { onScan: (d: LicenseData) => void; label?: string; small?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`btn ${small ? 'sm' : ''} ls-btn`} onClick={() => setOpen(true)}>
        <Icon name="barcode" size={16} /> {label}
      </button>
      {open && <LicenseScanDialog onClose={() => setOpen(false)} onUse={(d) => (onScan(d), setOpen(false))} />}
    </>
  );
}

function LicenseScanDialog({ onClose, onUse }: { onClose: () => void; onUse: (d: LicenseData) => void }) {
  const [mode, setMode] = useState<'camera' | 'scanner'>(camSupported() ? 'camera' : 'scanner');
  const [data, setData] = useState<LicenseData | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [matches, setMatches] = useState<any[] | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const box = useRef<HTMLTextAreaElement>(null);
  const [raw, setRaw] = useState('');

  // Camera: look for a PDF417 barcode a few times a second.
  useEffect(() => {
    if (mode !== 'camera' || data) return;
    let stop = false;
    let stream: MediaStream | null = null;
    (async () => {
      try {
        const formats: string[] = await (window as any).BarcodeDetector.getSupportedFormats?.().catch(() => []);
        if (formats && formats.length && !formats.includes('pdf417')) {
          setMode('scanner');
          setErr('This device’s camera can’t read license barcodes. Use a handheld scanner, or type the details.');
          return;
        }
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 1920 } } });
        if (stop) return;
        video.current!.srcObject = stream;
        await video.current!.play();
        const det = new (window as any).BarcodeDetector({ formats: ['pdf417'] });
        while (!stop) {
          const codes = await det.detect(video.current).catch(() => []);
          const hit = codes.map((c: any) => parseAamva(c.rawValue)).find(Boolean);
          if (hit) {
            setData(hit);
            break;
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch {
        setMode('scanner');
        setErr('The camera isn’t available. Use a handheld scanner, or type the details.');
      }
    })();
    return () => {
      stop = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [mode, data]);

  // Handheld scanner: it "types" the barcode into this box; read it once it stops typing.
  useEffect(() => {
    if (mode !== 'scanner' || data) return;
    box.current?.focus();
    if (!raw) return;
    const t = setTimeout(() => {
      const d = parseAamva(raw);
      if (d) setData(d);
      else if (raw.length > 30) setErr('That didn’t read as a license barcode. Try again.');
    }, 350);
    return () => clearTimeout(t);
  }, [raw, mode, data]);

  // Is this person already in BlueHQ?
  useEffect(() => {
    if (!data?.last) return;
    api
      .get<any>(`/api/people?q=${encodeURIComponent([data.first, data.last].filter(Boolean).join(' '))}`)
      .then((r) => setMatches((r.people ?? []).slice(0, 5)))
      .catch(() => setMatches([]));
  }, [data]);

  return (
    <div className="ls-back" role="dialog" aria-label="Scan a license" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="ls card">
        <div className="row between">
          <strong>Scan the back of the license</strong>
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        {!data && (
          <>
            <div className="seg-ctl" role="tablist" aria-label="How to scan">
              {camSupported() && (
                <button type="button" role="tab" aria-selected={mode === 'camera'} className={mode === 'camera' ? 'on' : ''} onClick={() => (setErr(null), setMode('camera'))}>
                  Camera
                </button>
              )}
              <button type="button" role="tab" aria-selected={mode === 'scanner'} className={mode === 'scanner' ? 'on' : ''} onClick={() => (setErr(null), setMode('scanner'))}>
                Handheld scanner
              </button>
            </div>
            {mode === 'camera' ? (
              <div className="ls-cam">
                <video ref={video} playsInline muted aria-label="Camera" />
                <div className="ls-aim" aria-hidden />
                <p className="small muted">Hold the wide barcode on the back of the license inside the box.</p>
              </div>
            ) : (
              <div className="stack sm">
                <p className="small">Pull the trigger on the barcode on the back of the license.</p>
                <textarea ref={box} className="ls-raw" value={raw} onChange={(e) => (setErr(null), setRaw(e.target.value))} aria-label="Scanner input" rows={3} placeholder="Waiting for the scanner…" />
              </div>
            )}
            {err && <p className="small ls-err">{err}</p>}
          </>
        )}
        {data && (
          <div className="stack sm">
            <dl className="kv ls-kv">
              <dt>Name</dt>
              <dd>{[data.first, data.middle, data.last, data.suffix].filter(Boolean).join(' ')}</dd>
              <dt>Date of birth</dt>
              <dd>{data.dob ?? '—'}</dd>
              <dt>Sex</dt>
              <dd>{data.sex ?? '—'}</dd>
              <dt>Address</dt>
              <dd>{data.address ?? '—'}</dd>
              <dt>License</dt>
              <dd className="mono">
                {data.dl ?? '—'} {data.dlState}
                {data.dlClass ? ` · class ${data.dlClass}` : ''}
                {data.expires ? ` · expires ${data.expires}` : ''}
              </dd>
              <dt>Height / weight</dt>
              <dd>{[data.height, data.weight && `${data.weight} lb`].filter(Boolean).join(' · ') || '—'}</dd>
              <dt>Eyes / hair</dt>
              <dd>{[data.eyes, data.hair].filter(Boolean).join(' / ') || '—'}</dd>
            </dl>
            {data.expires && data.expires < new Date().toISOString().slice(0, 10) && <p className="small ls-err">This license is expired.</p>}
            {matches && matches.length > 0 && (
              <div className="small ls-match">
                <strong>Already in BlueHQ:</strong> {matches.map((m) => m.name ?? m.full_name ?? m.display ?? '').filter(Boolean).join('; ')}. Check People &amp; vehicles for their history.
              </div>
            )}
            <div className="row gap-sm">
              <button type="button" className="btn primary" onClick={() => onUse(data)}>
                Use this
              </button>
              <button type="button" className="btn ghost" onClick={() => (setData(null), setRaw(''), setMatches(null))}>
                Scan again
              </button>
            </div>
            <p className="small muted">Check it against the license before you save.</p>
          </div>
        )}
      </div>
    </div>
  );
}
